import React, { useEffect, useRef, useState } from 'react';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FormDateRangeInput, FormTextInput, FormLocationSelector } from 'components/form-fields';
import { SetFiltering } from 'state/actions';
import { ListingTable } from 'components/listing/ListingTable';
import { FleetService, LinkService } from 'services';
import moment from 'moment';
import { Button, TextButton } from 'components/buttons';
import { StringToEllipsesString } from 'components/formatting/StringToEllipsesString';
import { Intent, Position } from '@blueprintjs/core';
import { NotificationToaster, NotificationInline } from 'components/notifications';
import { ShowHide } from 'components/layout';
import { EventViewModal } from './modals/EventViewModal';
import { EventEditModal } from 'pages/asset/events/modals/EventEditModal';
import classNames from 'classnames';
import { Filter, FilterButton } from 'components/filtering';
import { useDebounce } from 'hooks/useDebounce';
import { ReportList } from "components/reporting/ReportList";
import { AssetStatus } from 'components/status/AssetStatus';
import { InternalLink } from 'components/navigation';
import { Tooltip, } from 'components/tooltip';
import { AssetHelperFunctions } from 'helpers';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";

export function EventListing({
    asset,
    eventOptions,
    mappedAssetOptions,
    loading,
    eventTypes,
    pageState,
    locationList
}) {
    const viewAction = "FMS:View";
    const editAction = "FMS:Edit";
    const canEditPage = UserHasAccess(editAction);
    const debounceTimeoutMs = 750;
    const truncateLength = 15;
    const truncateLengthLong = 30;
    const defaultWeekStart = -2;
    const defaultTimeoutMs = 500;
    const hubFilterCount = 5;
    const filterColumnCount = pageState === "HUB" ? hubFilterCount : 2;
    const typeIdArrayPosition = 3;
    const statusIdArrayPosition = 4;

    const [loadingData, setLoadingData] = useState(true);
    const [isEventViewModalOpen, setIsEventViewModalOpen] = useState(false);
    const [isEventEditModalOpen, setIsEventEditModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [predefinedFilters, setPredefinedFilters] = useState([]);
    const [predefinedFiltersSetupComplete, setPredefinedFiltersSetupComplete] = useState(false);
    const [searchTerm, setSearchTerm] = useState(null);
    const [reportDownloading, setReportDownloading] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeoutMs);
    const dispatch = useDispatch();
    const inputSearchRef = useRef(null);
    const eventHubReduxName = "eventHubRedux" + pageState; //This is the shared redux for the hub
    const eventListingFilterReduxName = "eventListingFilterRedux" + pageState; //This is the redux for the filters
    const eventListingReduxName = "eventListingRedux" + pageState; //This is the page redux
    const dateFormat = "YYYY-MM-DD";
    const dateDisplayFormat = "DD/MM/YYYY";
    const actionHeader = ["Actions"];

    const tableHeadersProfileInital = [
        "Event Type",
        "Event Status",
        "Event Date",
        "Due Date",
        "Outstanding Requirements",
        "Start Date",
        "Completed Date"
    ];

    const tableHeadersHub = [
        "ID/Registration",
        "",
        "Fleet Number",
        "Location"
    ];

    const sortableHeaders = [
        { headerName: "ID/Registration", sortName: "assetregistration" },
        { headerName: "Fleet Number", sortName: "fleetnumber" },
        { headerName: "Location", sortName: "locationname" },
        { headerName: "Event Type", sortName: "eventtypename" },
        { headerName: "Event Status", sortName: "eventstatus" },
        { headerName: "Event Date", sortName: "eventdate" },
        { headerName: "Due Date", sortName: "duedate" },
        { headerName: "Start Date", sortName: "datestarted" },
        { headerName: "Completed Date", sortName: "completeddate" }
    ];

    // Table state
    const defaultRecordSize = 25;
    const tableHeadersProfile = canEditPage ? [...tableHeadersProfileInital, ...actionHeader] : tableHeadersProfileInital;
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableSortBy, setTableSortBy] = useState(sortableHeaders.find(sh => sh.headerName === "Event Date"));
    const [tableSortDir, setTableSortDir] = useState("A");
    const [startDate, setStartDate] = useState(moment().add(defaultWeekStart, 'w').format(dateFormat));
    const [endDate, setEndDate] = useState(moment().add(2, 'w').format(dateFormat));
    const [pageFilters, setPageFilters] = useState([]);
    const [reduxLoaded, setReduxLoaded] = useState(false);
    const [eventListingFilterReduxLoaded, setEventListingFilterReduxLoaded] = useState(false);
    const [eventHubReduxLoaded, setEventHubReduxLoaded] = useState(false);
    const [eventListingReduxLoaded, setEventListingReduxLoaded] = useState(false);
    const [focusToSearchTerm, setFocusToSearchTerm] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationDefaultsToTrue, setLocationDefaultsToTrue] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState({})
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});

    const currentPageSize = useRef();
    const currentPageNumber = useRef();
    const currentSortBy = useRef();
    const currentSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentSearchTerm = useRef();
    const currentSelectedLocations = useRef();

    const noDataMessage = "No results found, please try altering the search criteria.";
    const location = useLocation();

    const eventHubRedux = useSelector(state => state.filters[eventHubReduxName]);
    const eventListingRedux = useSelector(state => state.filters[eventListingReduxName]);
    const eventListingFilterRedux = useSelector(state => state.filters[eventListingFilterReduxName]) || [];

    if (eventListingFilterRedux.length === 0 && pageState === "HUB") {
        dispatch(SetFiltering(eventListingFilterReduxName, AssetHelperFunctions.defaultAssetStatusFilters));
    }

    useEffect(() => {
        if (eventListingFilterReduxLoaded && eventHubReduxLoaded && eventListingReduxLoaded) {
            setReduxLoaded(true);
        }
    }, [eventListingFilterReduxLoaded, eventHubReduxLoaded, eventListingReduxLoaded]);

    useEffect(() => {
        if (!eventListingFilterReduxLoaded) {
            setAppliedFilters(eventListingFilterRedux ?? []);
            setEventListingFilterReduxLoaded(true);
        }
    }, [eventListingFilterRedux]);

    useEffect(() => {
        if (!eventListingReduxLoaded) {
            setEventListingReduxLoaded(true);
            if (eventListingRedux) {
                setPageNumber(eventListingRedux.pageNumber);
                setPageSize(eventListingRedux.pageSize);
                setTableSortDir(eventListingRedux.tableSortDir);
                setTableSortBy(eventListingRedux.tableSortBy);
                setSearchTerm(eventListingRedux.searchTerm);
            } else {
                setSearchTerm("");
            }
        }
    }, [eventListingRedux]);

    useEffect(() => {
        if (!eventHubReduxLoaded) {
            setEventHubReduxLoaded(true);
            if (eventHubRedux) {
                setStartDate(eventHubRedux.startDate);
                setEndDate(eventHubRedux.endDate);
            }
            if (pageState === 'HUB') {
                if (eventHubRedux) {
                    setSelectedLocations(eventHubRedux.locations);
                } else {
                    setLocationDefaultsToTrue(true);
                }
            }
        }
    }, [eventHubRedux]);


    useEffect(() => {
        if (reduxLoaded && eventTypes && eventOptions?.eventStatuses && mappedAssetOptions) {
            const eventTypeFilters = pageState === 'HUB'
            ? eventTypes
            : eventTypes.map(et => ({
                id: String(et.eventTypeId ?? et.id),
                name: et.eventTypeName ?? et.name
            }));

            const statusItems = (eventOptions.eventStatuses || []).map(s => ({
                ...s,
                id: String(s.id)
            }));

            const filters = [
                {
                    name: "ASSETSTATUSID",
                    displayName: "Asset Status",
                    items: mappedAssetOptions.assetStatusesMappings
                },
                {
                    name: "ASSETOWNERSHIPID",
                    displayName: "Asset Ownership",
                    items: mappedAssetOptions.ownershipAttributeMappings
                },
                {
                    name: "ASSETTYPE",
                    displayName: "Asset Type",
                    items: mappedAssetOptions.assetTypes
                },
                {
                    name: "EVENTTYPEID",
                    displayName: "Event Type",
                    items: eventTypeFilters
                },
                {
                    name: "EVENTSTATUSID",
                    displayName: "Event Status",
                    items: statusItems
                }
            ];

            // Read URL params and set selected
            const params = new URLSearchParams(location.search);
            const eventTypeId = params.get('eventTypeId');
            const eventStatusIdParam = params.get('eventStatusId');
            const selectedFilters = [];

            if (eventTypeId) {
                const etItem = eventTypeFilters.find(i => i.id === parseInt(eventTypeId));
                const typeId = String(eventTypeId);
                filters[typeIdArrayPosition].items = filters[typeIdArrayPosition].items.map(item => ({ ...item, selected: item.id === typeId }));
                selectedFilters.push({
                    name: "EVENTTYPEID",
                    value: typeId,
                    displayName: etItem?.name || "Event Type"
                });
            }

            if (eventStatusIdParam) {
                const eventStatusIds = eventStatusIdParam.split(',').map(s => s.trim()).filter(Boolean).map(String);
                filters[statusIdArrayPosition].items = filters[statusIdArrayPosition].items.map(item => ({
                    ...item,
                    selected: eventStatusIds.includes(item.id)
                }));
                selectedFilters.push(...eventStatusIds.map(id => ({
                    name: "EVENTSTATUSID",
                    value: id,
                    displayName: statusItems.find(s => s.id === id)?.name || "Event Status"
                })));
            }

            if (eventTypeId || eventStatusIdParam) {
                //We need to reset the page
                setPageNumber(1);
                setSearchTerm("");
                setEventListingDispatch("", 1);
            }

            if (pageState === "HUB") {
                setPageFilters(filters);
            } else {
                setPageFilters(filters.filter(f => !["ASSETSTATUSID", "ASSETOWNERSHIPID", "ASSETTYPE"].includes(f.name)));
            }

            if (selectedFilters.length > 0) {
                dispatch(SetFiltering(eventListingFilterReduxName, selectedFilters));
                setAppliedFilters(selectedFilters);
            }

        }
    }, [eventTypes, eventOptions, location.search, pageState, dispatch, reduxLoaded, mappedAssetOptions]);

    useEffect(() => {
        if (pageState !== 'HUB' && asset?.assetLocation?.location?.id) {
            setPredefinedFilters([
                { key: "AssetId", value: `${asset.assetId}` },
                { key: "Location", value: `${asset?.assetLocation?.location?.id}` },
            ]);
            setSelectedAsset(asset);
        }
    }, [asset, pageState]);

    useEffect(() => {
        if (tableRows) {
            setFocusToSearchTerm(true);
        }
    }, [tableRows]);

    useEffect(() => {
        if (predefinedFilters && predefinedFilters.length > 0) {
            setPredefinedFiltersSetupComplete(true);
        }
    }, [predefinedFilters]);

    useEffect(() => {
        if (pageState === 'HUB') {
            setPredefinedFiltersSetupComplete(true);
        }
    }, []);

    useEffect(() => {
        if (!loading && reduxLoaded && predefinedFiltersSetupComplete && selectedLocations) {
            fetchTableData();
        }
    }, [loading, pageSize, pageNumber, tableSortBy, tableSortDir, appliedFilters, startDate, endDate, debouncedSearchTerm, reduxLoaded, predefinedFiltersSetupComplete, selectedLocations]);

    useEffect(() => {
        if (focusToSearchTerm && !loading) {
            setTimeout(function () {
                if (inputSearchRef.current != null) {
                    inputSearchRef.current.focus();
                }
            }, defaultTimeoutMs);
            setFocusToSearchTerm(false);
        }
    }, [focusToSearchTerm, loading]);

    function mapEventsToTable(events) {
        const rows = events.map((event, index) => {
            let status = eventOptions.eventStatuses.find(es => es.name === event.eventStatus);
            let et = pageState === 'HUB' ? eventTypes.find(e => e.id === event.eventTypeId) : eventTypes.find(e => e.eventTypeId === event.eventTypeId);
            let iconJsx = <></>;
            if (et) {
                iconJsx = <i className={classNames("custom-icon-" + et.iconName, "custom-icon keyblock-icon")} />
            }
            let eventDateClass = status && !status?.startedStatus && moment(event.eventDate).startOf('day').isSameOrBefore(moment().startOf('day')) ? "text-intent-danger" : "";
            let dueDateClass = event.dueDate && moment(event.dueDate).startOf('day').isBefore(moment().startOf('day')) ? "text-intent-danger" : "";
            let assetSubtype = null;
            if (event.assetTypeSubtypeId && event.assetTypeSubtypeId > 0) {
                let assetType = mappedAssetOptions.assetTypes.find(a => a.id === event.assetTypeId)
                assetSubtype = assetType?.subtypes ? assetType.subtypes.find(st => st.id === event.assetTypeSubtypeId) : null;
            }

            let row = [];
            let rowAsset = null;
            if (pageState === 'HUB') {
                rowAsset = ({
                    registration: event.assetRegistration,
                    assetLocation: {
                        assetStatus: {
                            id: event.assetStatusId
                        },
                        assetOwnership: {
                            id: event.assetOwnershipId
                        }
                    }
                });
                row.push(
                    <div className="nowrap inline-items" key={`${event.eventId}-${index}`}>
                        <AssetStatus statusId={event.assetStatusId} ownershipId={event.assetOwnershipId} isLoading={loading} statusAttributeMappings={mappedAssetOptions.assetStatusesMappings} ownershipAttributeMappings={mappedAssetOptions.ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                        <Tooltip position={Position.TOP} content={`Asset Profile`}>
                            <InternalLink link={LinkService.getVehicleProfileUrl(event.assetId, 'events', "area=MMFH")}>{event.assetRegistration}</InternalLink>
                        </Tooltip>
                    </div>,
                    <div className="nowrap inline-items" key={`asset-icon-${event.eventId}-${index}`}>
                        {
                            AssetHelperFunctions.getVehicleIcon(
                                event.assetTypeId,
                                event.assetTypeName,
                                assetSubtype?.name)
                        }
                    </div>,
                    event.fleetNumber,
                    event.locationName
                );
            }
            row.push(<span className="nowrap">{iconJsx} <TextButton onClick={() => onClickViewEvent(event, rowAsset)} className="text-align-left" key={`view-event-${event.eventId}`}>
                <StringToEllipsesString length={truncateLengthLong} showTooltip={true}>{event.eventTypeName}</StringToEllipsesString>
            </TextButton></span>);
            row.push(event.eventStatus,
                event.eventDate ? <span className={eventDateClass}>{moment(event.eventDate).format(dateDisplayFormat)}</span> : "",
                event.dueDate ? <span className={dueDateClass}>{moment(event.dueDate).format(dateDisplayFormat)}</span> : "",
                event.outstandingRequirements ? <StringToEllipsesString length={truncateLength} showTooltip={true}>{event.outstandingRequirements.join(", ")}</StringToEllipsesString> : "",
                event.startDate ? moment(event.startDate).format(dateDisplayFormat) : "",
                event.completedDate ? moment(event.completedDate).format(dateDisplayFormat) : "",
                status?.completedStatus ? null :
                    <UserAccess
                        key={event.eventId}
                        perform={editAction}
                        yes={() => (
                            <Tooltip key={`edit-${event.id}`} content={`Edit`}>
                                <Button
                                    onClick={(e) => onEditEvent(event, e, rowAsset)}
                                    icon="edit"
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                />
                            </Tooltip>
                        )}
                        no={() => null}
                    />);

            return row;
        });
        setTableRows(rows);
    }

    function fetchTableData(forceReload) {
        if ((!reduxLoaded ||
            //Need to do this check as we need to use the debouncedSearchTerm, but we need to only load once if the seachTerm is set in the redux so once it's set we know we have the redux all loaded
            searchTerm === null ||
            (searchTerm?.length > 0 && debouncedSearchTerm === null) ||
            !appliedFilters) ||
            !forceReload &&
            currentPageSize.current === pageSize &&
            currentPageNumber.current === pageNumber &&
            currentSortBy.current === tableSortBy &&
            currentSortDir.current === tableSortDir &&
            currentAppliedFilters.current === appliedFilters.length &&
            currentStartDate.current === startDate &&
            currentEndDate.current === endDate &&
            currentSelectedLocations.current === selectedLocations &&
            (currentSearchTerm.current ?? "") === (debouncedSearchTerm ?? "")
        ) {
            return;
        }

        if (pageNumber > 1 && (currentSearchTerm.current ?? "") !== (debouncedSearchTerm ?? "")) {
            //Can't set the page number when this changes else the debounce doesn't work. The page number change should kick this off again.
            setPageNumber(1);
            return;
        }

        setLoadingData(true);

        currentPageSize.current = pageSize;
        currentPageNumber.current = pageNumber;
        currentSortBy.current = tableSortBy;
        currentSortDir.current = tableSortDir;
        currentAppliedFilters.current = appliedFilters.length;
        currentStartDate.current = startDate;
        currentEndDate.current = endDate;
        currentSelectedLocations.current = selectedLocations;
        currentSearchTerm.current = (debouncedSearchTerm ?? "");

        const allFilters = [
        ];

        if (appliedFilters && appliedFilters.length > 0) {
            allFilters.push(...appliedFilters.map(f => {
                if (f.name === "AssetTypeSubtype" && typeof f.value === "string" && f.value.startsWith("uncat-")) {
                    return { key: "AssetType", value: f.parentAssetTypeId.toString() };
                }
                return { key: f.name, value: String(f.value) };
            }));
        }

        if (pageState === 'HUB') {
            if (selectedLocations && selectedLocations.length > 0) {
                allFilters.push(...selectedLocations.map(sl => ({ key: "Location", value: `${sl}` })));
            } else {
                // We need this so the back end knows this is an empty filter vs not having locations, which is what the app sends in
                allFilters.push({ key: "Location", value: "-1" });
            }
        } else {
            allFilters.push(...predefinedFilters);
        }

        FleetService.searchEvents(
            startDate,
            endDate,
            pageSize,
            pageNumber,
            debouncedSearchTerm === null ? searchTerm : debouncedSearchTerm.trim(),
            tableSortBy.sortName,
            tableSortDir,
            allFilters
        )
            .then(response => {
                mapEventsToTable(response.data);
                setTotalRecords(response.totalCount);
            })
            .catch(error => {
                NotificationToaster.show(Intent.DANGER, "Could not fetch events, please try again.");
            })
            .finally(() => {
                setLoadingData(false);
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
            });
    }

    function onClickViewEvent(event, clickAsset) {
        if (pageState === 'HUB' && clickAsset) {
            setSelectedAsset(clickAsset);
        }
        setSelectedEvent(event);
        setIsEventViewModalOpen(true);
    }

    function onEventViewModalClose() {
        setSelectedEvent(null);
        setIsEventViewModalOpen(false);
    }

    function hideEventEditModal() {
        setIsEventEditModalOpen(false);
    }

    function onEditEvent(event, e, clickAsset) {
        if (pageState === 'HUB' && clickAsset) {
            setSelectedAsset(clickAsset);
        }
        e.stopPropagation();
        setSelectedEvent(event);
        setIsEventEditModalOpen(true);

        // if we are currently viewing event then close the view modal
        if (isEventViewModalOpen === true) {
            setIsEventViewModalOpen(false);
        }
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setEventListingDispatch(item);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
        setEventListingDispatch(null, newPageNumber, newPageSize);
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
        setEventListingDispatch(null, null, null, header, direction);
    }

    function setEventListingDispatch(newSearchTerm, newPageNumber, newPageSize, newTableSortBy, newTableSortDir) {
        dispatch(SetFiltering(eventListingReduxName, {
            searchTerm: newSearchTerm ?? searchTerm,
            pageNumber: newPageNumber ?? pageNumber,
            pageSize: newPageSize ?? pageSize,
            tableSortBy: newTableSortBy ?? tableSortBy,
            tableSortDir: newTableSortDir ?? tableSortDir
        }));
    }

    function onDateRangeChange(item) {
        const tempStartDate = item.startDate.date ? item.startDate.date.format(dateFormat) : "";
        const tempEndDate = item.endDate.date ? item.endDate.date.format(dateFormat) : "";

        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
        setEventHubDispatch(tempStartDate, tempEndDate);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setEventHubDispatch(null, null, locations);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function setEventHubDispatch(newStartDate, newEndDate, newLocations) {
        dispatch(SetFiltering(eventHubReduxName, {
            startDate: newStartDate ?? startDate,
            endDate: newEndDate ?? endDate,
            locations: newLocations ?? selectedLocations
        }));
    }

    function onFilterChange(listingFilters) {
        if (initialLoadComplete) {
            setPageNumber(1);
        }
        setAppliedFilters(listingFilters ?? []);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    return (
        <UserAccess perform={viewAction}
            yes={() => (
                <>
                    <NotificationInline
                        intent="success"
                        text="Your report has been queued, and will download as soon as it's ready."
                        show={reportDownloading}
                    />
                    <div className="filter-controls spacer-bottom-small inline-items">
                        <FormDateRangeInput
                            onChange={onDateRangeChange}
                            startDate={startDate}
                            endDate={endDate}
                            disabled={loading || loadingData}
                            allowClear={pageState !== 'HUB'}
                            large
                        />
                        <ShowHide
                            evaluator={pageState === 'HUB'}
                            show={(
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput inputRef={inputSearchRef} placeholder="Search fleet" onChange={(x) => onSearchTextChange(x.target.value)} value={searchTerm} large disabled={loading || loadingData} icon="search" id="user-search-field" />
                                    </div>
                                </div>
                            )}
                        />
                        <ShowHide
                            evaluator={pageState === 'HUB'}
                            show={(
                                <FormLocationSelector
                                    businessArea=""
                                    loading={loading || loadingData}
                                    selectedLocations={selectedLocations}
                                    setSelectedLocations={onSelectedLocationsChange}
                                    useHierarchy={false}
                                    useCustomLocations
                                    customLocations={locationList}
                                    useLocationDefaults={locationDefaultsToTrue}
                                    locationDefaultsToTrue={locationDefaultsToTrue}
                                />
                            )}
                        />
                        <ReportList
                            pageName="Asset-Events"
                            lastSearchPayload={{
                                assetId: asset?.assetId,
                                startDate,
                                endDate,
                                appliedFilters,
                                searchTerm: pageState === "HUB" ? searchTerm : undefined,
                                selectedLocations: pageState === "HUB" ? selectedLocations : undefined
                            }}
                            onReportDownloadingChange={setReportDownloading}
                            disabled={loadingData || loading}
                            compactLarge
                        />
                        <Filter
                            filterName={eventListingFilterReduxName}
                            visible={showFilters && reduxLoaded}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            loading={loading}
                            onClickOutside={onToggleFilter}
                            numOfColumns={filterColumnCount}
                            customRenderers={{
                                ASSETTYPE: (props) => (
                                    <AssetTypeFilterRenderer
                                        key="asset-Type-filter-renderer"
                                        {...props}
                                        expandedAssetTypes={expandedAssetTypes}
                                        setExpandedAssetTypes={setExpandedAssetTypes}
                                    />
                                )
                            }}
                        />
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>

                    <ListingTable
                        headers={pageState === 'HUB' ? [...tableHeadersHub, ...tableHeadersProfile] : tableHeadersProfile}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={loadingData || loading}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                        pageSize={pageSize}
                        startPage={pageNumber}
                    />

                    <ShowHide
                        evaluator={isEventViewModalOpen}
                        show={(
                            <EventViewModal
                                eventId={selectedEvent?.eventId}
                                eventTypeId={selectedEvent?.eventTypeId}
                                isOpen={isEventViewModalOpen}
                                onModalClose={onEventViewModalClose}
                                asset={selectedAsset}
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading && loadingData}
                                refetchEvents={() => fetchTableData(true)}
                                eventTypesForAsset={eventTypes}
                                openEditEventModal={(e) => onEditEvent(selectedEvent, e)}
                                requiredAction={viewAction}
                            />
                        )}
                    />

                    <ShowHide
                        evaluator={isEventEditModalOpen}
                        show={(
                            <EventEditModal
                                isOpen={isEventEditModalOpen}
                                hideModal={hideEventEditModal}
                                onSuccess={() => fetchTableData(true)}
                                eventId={selectedEvent?.eventId}
                                eventTypeId={selectedEvent?.eventTypeId}
                                asset={selectedAsset}
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading && loadingData}
                            />
                        )}
                    />
                </>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}

EventListing.propTypes = {
    asset: PropTypes.object,
    eventOptions: PropTypes.object,
    mappedAssetOptions: PropTypes.object,
    loading: PropTypes.bool,
    eventTypes: PropTypes.array,
    pageState: PropTypes.oneOf(['PROFILE', 'HUB']).isRequired,
    locationList: PropTypes.object
};